import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Play,
  Check,
  Trash2,
  Calendar,
  Clock,
  Plus,
  Bus
} from "lucide-react";
import {
  getSchedulerConfig,
  updateSchedulerConfig,
  requestSchedulerApproval,
  approveSchedulerConfig,
  pauseScheduler,
  listSchedulerRuns,
  runSchedulerNow,
  addSchedulerSchedule,
  removeSchedulerSchedule,
  approveSchedulerSchedule,
  approveSchedulerSchedulePortal,
  getSchedulerConfigPortal,
  updateSchedulerConfigPortal,
  requestSchedulerApprovalPortal,
  pauseSchedulerPortal,
  listSchedulerRunsPortal,
  addSchedulerSchedulePortal,
  removeSchedulerSchedulePortal
} from "../../api/fees/feeSchedulerApi";
import {
  getFeeStructuresByYear,
  getFeeStructuresByYearPortal,
  getAllFeeHeads,
  getAllFeeHeadsPortal
} from "../../api/fees/feeApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal
} from "../../api/academicyear/academicYearApi";
import {
  getAllClasses
} from "../../api/student/studentApi";
import {
  getAllClassesForPortal
} from "../../api/student/studentPortalApi";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Table,
  EmptyState,
  Skeleton,
  Checkbox
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const isTransportHead = (h) =>
  h && (h.type === "TRANSPORT" || h.source === "TRANSPORT_MODULE");

/** Normalize API list payloads (array | { data } | { data: { data } }). */
const unwrapList = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const structureLabel = (s) => {
  if (s.name) return s.name;
  const head = s.feeHeadId?.name || s.feeHeadName || "";
  const amount = s.amount != null ? `₹${s.amount}` : "";
  return [head, amount].filter(Boolean).join(" · ") || "Structure";
};

const FeeSchedulerPage = ({ isPortal = false, embedded = false }) => {
  const [cfg, setCfg] = useState(null);
  const [years, setYears] = useState([]);
  const [structures, setStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [transportHeads, setTransportHeads] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [viewerCanApprove, setViewerCanApprove] = useState(false);

  const toast = useToast();

  const [form, setForm] = useState({
    month: MONTHS[new Date().getMonth()],
    year: new Date().getFullYear(),
    publishDate: "",
    feeStructureIds: [],
    includeTransport: true,
  });

  const api = {
    getConfig: isPortal ? getSchedulerConfigPortal : getSchedulerConfig,
    updateConfig: isPortal ? updateSchedulerConfigPortal : updateSchedulerConfig,
    requestApproval: isPortal ? requestSchedulerApprovalPortal : requestSchedulerApproval,
    pause: isPortal ? pauseSchedulerPortal : pauseScheduler,
    listRuns: isPortal ? listSchedulerRunsPortal : listSchedulerRuns,
    years: isPortal ? getAllAcademicYearsPortal : getAllAcademicYears,
    structures: isPortal ? getFeeStructuresByYearPortal : getFeeStructuresByYear,
    classes: isPortal ? getAllClassesForPortal : getAllClasses,
    feeHeads: isPortal ? getAllFeeHeadsPortal : getAllFeeHeads,
    addSchedule: isPortal ? addSchedulerSchedulePortal : addSchedulerSchedule,
    removeSchedule: isPortal ? removeSchedulerSchedulePortal : removeSchedulerSchedule,
    approveSchedule: isPortal ? approveSchedulerSchedulePortal : approveSchedulerSchedule,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, yRes, rRes, clRes, headsRes] = await Promise.all([
        api.getConfig(),
        api.years(),
        api.listRuns({ limit: 20 }),
        api.classes().catch(() => []),
        api.feeHeads({ isActive: true }).catch(() => []),
      ]);
      const c = cRes?.data || cRes || {};
      setCfg({
        enabled: !!c.enabled,
        academicYearId: c.academicYearId || "",
        pausedUntil: c.pausedUntil ? String(c.pausedUntil).slice(0, 10) : "",
        approvalStatus: c.approvalStatus || "DRAFT",
        schedules: c.schedules || [],
      });
      setViewerCanApprove(!!cRes?.viewerCanApprove);
      setYears(unwrapList(yRes));
      setRuns(unwrapList(rRes));
      setClasses(unwrapList(clRes));
      const heads = unwrapList(headsRes);
      setTransportHeads(heads.filter((h) => isTransportHead(h)));
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load scheduler");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!cfg?.academicYearId) {
      setStructures([]);
      return;
    }
    api
      .structures({ academicYearId: cfg.academicYearId })
      .then((res) => {
        setStructures(unwrapList(res));
      })
      .catch(() => setStructures([]));
  }, [cfg?.academicYearId, isPortal]);

  const takenKeys = useMemo(() => {
    const set = new Set();
    (cfg?.schedules || []).forEach((s) => {
      if (s.status !== "CANCELLED") set.add(`${s.month}|${s.year}`);
    });
    return set;
  }, [cfg]);

  // String-keyed lookup so ObjectId / string mismatches still resolve class names
  const classNameLookup = useMemo(() => {
    const map = {};
    classes.forEach((c) => {
      if (!c) return;
      const id = String(c._id ?? c.id ?? "");
      if (!id) return;
      const name = c.className || c.name || c.classCode;
      map[id] = name ? String(name) : `Class ${id}`;
    });
    return map;
  }, [classes]);

  const structureRows = useMemo(() => {
    const byClass = {};
    structures.forEach((s) => {
      const head = s.feeHeadId || {};
      // Transport is module-driven — never select as a fee structure (same as Generate Batch)
      if (isTransportHead(head)) return;

      const clsId = String(s.classId?._id || s.classId || "");
      if (!clsId) return;

      const clsLabel =
        s.classId?.className ||
        s.classId?.name ||
        classNameLookup[clsId] ||
        s.className ||
        `Class ${clsId}`;

      byClass[clsId] = byClass[clsId] || {
        classId: clsId,
        label: clsLabel,
        items: [],
      };
      byClass[clsId].items.push(s);
    });
    return Object.values(byClass).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true })
    );
  }, [structures, classNameLookup]);

  const transportHeadLabel =
    transportHeads.length === 0
      ? "No transport fee head configured"
      : transportHeads.map((h) => h.name).join(", ");

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.updateConfig({
        enabled: cfg.enabled,
        academicYearId: cfg.academicYearId || null,
        pausedUntil: cfg.pausedUntil || null,
      });
      toast.success("Settings saved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addSchedule = async (e) => {
    e.preventDefault();
    if (takenKeys.has(`${form.month}|${form.year}`)) {
      toast.error(`Schedule for ${form.month} ${form.year} already exists`);
      return;
    }
    if (!form.publishDate) {
      toast.error("Select publish date");
      return;
    }
    if (!form.feeStructureIds.length && !form.includeTransport) {
      toast.error(
        "Select at least one fee structure, or enable Include transport fees."
      );
      return;
    }
    if (form.includeTransport && transportHeads.length === 0) {
      toast.error(
        "No active Transport fee head found. Create a fee head with type TRANSPORT first."
      );
      return;
    }
    setSaving(true);
    try {
      await api.addSchedule({
        academicYearId: cfg.academicYearId,
        month: form.month,
        year: Number(form.year),
        publishDate: form.publishDate,
        feeStructureIds: form.feeStructureIds,
        includeTransport: Boolean(form.includeTransport),
      });
      toast.success("Schedule added");
      setForm((f) => ({
        ...f,
        feeStructureIds: [],
        includeTransport: true,
      }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add schedule");
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    try {
      await runSchedulerNow();
      toast.success("Job queued");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Run failed");
    }
  };

  const approveSchedule = async (id) => {
    setApprovingId(id);
    try {
      await api.approveSchedule(id);
      toast.success("Schedule approved — it will publish on its publish date.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Approve failed");
    } finally {
      setApprovingId(null);
    }
  };

  const removeSchedule = async (id) => {
    if (!window.confirm("Remove this schedule?")) return;
    try {
      await api.removeSchedule(id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Remove failed");
    }
  };

  const toggleStructure = (id) => {
    setForm((f) => {
      const set = new Set(f.feeStructureIds.map(String));
      const s = String(id);
      if (set.has(s)) set.delete(s);
      else set.add(s);
      return { ...f, feeStructureIds: Array.from(set) };
    });
  };

  const toggleClassRow = (groupIds, allChecked) => {
    setForm((f) => {
      const set = new Set(f.feeStructureIds.map(String));
      if (allChecked) groupIds.forEach((id) => set.delete(id));
      else groupIds.forEach((id) => set.add(id));
      return { ...f, feeStructureIds: Array.from(set) };
    });
  };

  const academicYearOptions = useMemo(
    () => [
      { label: "Select Academic Year", value: "" },
      ...years.map((y) => ({
        label: y.name || y.yearName || y._id,
        value: y._id,
      })),
    ],
    [years]
  );

  const monthOptions = useMemo(
    () =>
      MONTHS.map((m) => ({
        label: `${m}${takenKeys.has(`${m}|${form.year}`) ? " (exists)" : ""}`,
        value: m,
        disabled: takenKeys.has(`${m}|${form.year}`),
      })),
    [form.year, takenKeys]
  );

  const yearOptions = useMemo(
    () =>
      [form.year - 1, form.year, form.year + 1].map((y) => ({
        label: String(y),
        value: String(y),
      })),
    [form.year]
  );

  const schedulesColumns = [
    {
      key: "month",
      header: "Billing Month",
      render: (s) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">
          {s.month} {s.year}
        </span>
      ),
    },
    {
      key: "publishDate",
      header: "Publish Date",
      render: (s) =>
        s.publishDate
          ? new Date(s.publishDate).toLocaleDateString("en-IN")
          : "—",
    },
    {
      key: "structures",
      header: "Fee Structures",
      render: (s) => (
        <span>
          {(s.feeStructureIds || []).length}
          {s.includeTransport !== false ? " + transport" : ""}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (s) => {
        const pending =
          s.createdByType === "Staff" && s.approvalStatus === "PENDING_APPROVAL";
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge
              variant={
                s.status === "SCHEDULED"
                  ? "primary"
                  : s.status === "COMPLETED"
                  ? "success"
                  : "secondary"
              }
            >
              {s.status}
            </Badge>
            {pending && (
              <Badge variant="warning" size="sm">
                Awaiting Approval
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: "createdBy",
      header: "Created By",
      render: (s) => s.createdByType || "User",
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (s) => {
        const pending =
          s.createdByType === "Staff" && s.approvalStatus === "PENDING_APPROVAL";
        return (
          <div className="flex items-center justify-end gap-2">
            {viewerCanApprove && pending && (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                isLoading={approvingId === s._id}
                onClick={() => approveSchedule(s._id)}
                className="text-success-700 hover:bg-success-50"
              >
                Approve
              </Button>
            )}
            {s.status === "SCHEDULED" && (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => removeSchedule(s._id)}
                className="text-danger-600 hover:bg-danger-50"
              >
                Remove
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const runsColumns = [
    {
      key: "when",
      header: "When",
      render: (r) =>
        r.createdAt ? new Date(r.createdAt).toLocaleString() : "—",
    },
    {
      key: "month",
      header: "Billing Period",
      render: (r) => `${r.calendarMonth}/${r.calendarYear}`,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge
          variant={
            r.status === "SUCCESS"
              ? "success"
              : r.status === "FAILED"
              ? "danger"
              : "secondary"
          }
        >
          {r.status}
        </Badge>
      ),
    },
    {
      key: "details",
      header: "Details",
      render: (r) => (
        <span className="text-xs text-neutral-600 dark:text-neutral-300">
          {r.reason || r.error || ""}
          {r.stats?.studentsBilled != null && r.status === "SUCCESS"
            ? ` · ${r.stats.studentsBilled} students · ${r.stats.classCount ?? 0} classes · ${r.stats.sectionCount ?? 0} sections · ₹${r.stats.totalAmount ?? 0}`
            : ""}
        </span>
      ),
    },
  ];

  if (loading || !cfg) {
    return (
      <div className={embedded ? "space-y-4" : "p-4 sm:p-6 max-w-5xl mx-auto space-y-4"}>
        <Skeleton height={32} width="40%" />
        <Skeleton height={180} width="100%" />
        <Skeleton height={240} width="100%" />
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-6" : "p-4 sm:p-6 max-w-5xl mx-auto space-y-6"}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          {embedded ? (
            <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Auto Scheduler</h2>
          ) : (
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Fee Scheduler</h1>
          )}
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            Schedule monthly fee bills by month & publish date. Auto-publishes via Redis background job.
          </p>
        </div>
        <div>
          <Badge
            variant={
              cfg.approvalStatus === "APPROVED"
                ? "success"
                : cfg.approvalStatus === "PENDING_APPROVAL"
                ? "warning"
                : "secondary"
            }
            size="lg"
          >
            {cfg.approvalStatus} {cfg.enabled ? "· Enabled" : "· Disabled"}
          </Badge>
        </div>
      </div>

      
      

      {/* Setup Config Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Setup Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2.5 text-sm font-medium text-neutral-800 dark:text-neutral-100 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-neutral-300 dark:border-neutral-700 text-primary-900 dark:text-primary-300 focus:ring-primary-900 h-4 w-4"
              checked={cfg.enabled}
              onChange={(e) => setCfg((p) => ({ ...p, enabled: e.target.checked }))}
            />
            Enable scheduler (owner-created schedules run automatically; staff-created ones need approval below)
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <Select
              label="Academic Year"
              options={academicYearOptions}
              value={cfg.academicYearId}
              onChange={(val) => setCfg((p) => ({ ...p, academicYearId: val }))}
            />
            <Input
              type="date"
              label="Pause Until"
              value={cfg.pausedUntil || ""}
              onChange={(e) => setCfg((p) => ({ ...p, pausedUntil: e.target.value }))}
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-100 dark:border-neutral-800">
            <Button
              type="button"
              variant="primary"
              size="sm"
              isLoading={saving}
              onClick={saveSettings}
            >
              Save Settings
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={saving}
              onClick={() =>
                api
                  .requestApproval()
                  .then(load)
                  .catch((e) => toast.error(e.response?.data?.message || "Request failed"))
              }
            >
              Request Approval
            </Button>
            {!isPortal && (
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                disabled={saving}
                onClick={() =>
                  approveSchedulerConfig({ enabled: true })
                    .then(() => {
                      toast.success("Approved");
                      load();
                    })
                    .catch((e) => toast.error(e.response?.data?.message || "Approve failed"))
                }
                leftIcon={<Check className="h-4 w-4" />}
              >
                Approve & Enable
              </Button>
            )}
            {!isPortal && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={saving}
                onClick={handleRunNow}
                leftIcon={<Play className="h-4 w-4" />}
              >
                Run Now
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Schedule Card — aligned with Generate Batch modal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Plus className="h-4 w-4 text-primary-900 dark:text-primary-300" /> Add Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={addSchedule} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Select
                label="Billing Month"
                options={monthOptions}
                value={form.month}
                onChange={(val) => setForm((f) => ({ ...f, month: val }))}
              />
              <Select
                label="Year"
                options={yearOptions}
                value={String(form.year)}
                onChange={(val) => setForm((f) => ({ ...f, year: Number(val) }))}
              />
              <Input
                type="date"
                label="Publish Date"
                required
                value={form.publishDate}
                onChange={(e) => setForm((f) => ({ ...f, publishDate: e.target.value }))}
              />
            </div>

            {/* ── Structure-based heads (same UX as Generate Batch) ── */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wide">
                  Classes & Fee Heads
                </label>
                <Badge variant="primary">
                  {form.feeStructureIds.length} fee head
                  {form.feeStructureIds.length === 1 ? "" : "s"} selected
                </Badge>
              </div>

              {!cfg.academicYearId && (
                <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 p-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
                  Select an academic year above to view available fee structures.
                </div>
              )}

              {cfg.academicYearId && structureRows.length === 0 && (
                <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 p-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
                  No fee structures found for this academic year.
                </div>
              )}

              {structureRows.length > 0 && (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 divide-y divide-neutral-100 dark:divide-neutral-800">
                  {structureRows.map((row) => {
                    const groupIds = row.items.map((s) => String(s._id));
                    const selectedCount = groupIds.filter((id) =>
                      form.feeStructureIds.map(String).includes(id)
                    ).length;
                    const allChecked = selectedCount === groupIds.length;
                    const someChecked = selectedCount > 0 && !allChecked;

                    return (
                      <div
                        key={row.classId}
                        className="p-3 flex flex-col gap-2.5 sm:flex-row sm:items-start sm:justify-between"
                      >
                        <div className="shrink-0 w-36">
                          <Checkbox
                            label={
                              <span className="font-medium text-neutral-900 dark:text-neutral-100">
                                {row.label}
                              </span>
                            }
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={() => toggleClassRow(groupIds, allChecked)}
                          />
                          <p className="pl-6 text-[11px] text-neutral-400 dark:text-neutral-500">
                            {selectedCount}/{groupIds.length} selected
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-1.5 flex-1 justify-start">
                          {row.items.map((s) => {
                            const checked = form.feeStructureIds
                              .map(String)
                              .includes(String(s._id));
                            return (
                              <button
                                type="button"
                                key={s._id}
                                onClick={() => toggleStructure(s._id)}
                                className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 font-medium transition-colors border focus:outline-none focus:ring-2 focus:ring-primary-400 ${ checked ? "bg-primary-900 text-white border-primary-900" : "bg-neutral-50 dark:bg-neutral-950 text-neutral-700 dark:text-neutral-200 border-neutral-200 dark:border-neutral-800 hover:border-primary-400" }`}
                              >
                                <span>{structureLabel(s)}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Transport (module-driven amount) — same as Generate Batch ── */}
            <div
              className={`rounded-lg border p-3 space-y-2 ${ form.includeTransport ? "border-primary-200 bg-primary-50/60" : "border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950" }`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <Checkbox
                    checked={form.includeTransport}
                    onChange={(checked) =>
                      setForm((p) => ({
                        ...p,
                        includeTransport:
                          typeof checked === "boolean"
                            ? checked
                            : !p.includeTransport,
                      }))
                    }
                    disabled={transportHeads.length === 0}
                    label={
                      <span className="font-medium text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-1.5">
                        <Bus className="h-4 w-4 text-primary-700 dark:text-primary-400" />
                        Include transport fees
                      </span>
                    }
                  />
                </div>
              </div>

              <p className="text-xs text-neutral-600 dark:text-neutral-300 pl-6">
                {transportHeads.length === 0 ? (
                  <>
                    Create an active fee head with type <strong>TRANSPORT</strong>{" "}
                    first. Amount is never entered here — it comes from each
                    student&apos;s active transport assignment.
                  </>
                ) : (
                  <>
                    Fee head: <strong>{transportHeadLabel}</strong>
                    <br />
                    Amount is taken automatically from each student&apos;s
                    active transport assignment (not from fee structures).
                  </>
                )}
              </p>
            </div>

            <Button
              type="submit"
              variant="primary"
              isLoading={saving}
              className="w-full sm:w-auto"
            >
              Add Schedule
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Schedules Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Schedules</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table
            columns={schedulesColumns}
            data={cfg.schedules || []}
            emptyState={
              <EmptyState
                icon={<Calendar className="h-8 w-8 text-neutral-400 dark:text-neutral-500" />}
                title="No schedules added"
                description="Use the form above to add billing schedules."
              />
            }
          />
        </CardContent>
      </Card>

      {/* Run History Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Run History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table
            columns={runsColumns}
            data={runs}
            emptyState={
              <EmptyState
                icon={<Clock className="h-8 w-8 text-neutral-400 dark:text-neutral-500" />}
                title="No scheduler runs"
                description="Run history will appear here once auto-publishing triggers."
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default FeeSchedulerPage;
